import React, { useEffect, useState, useRef } from 'react';
import { Spin, Button, Result } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from './styles/SubscriptionsPage.styles';
import apiService from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 60000;

/**
 * Страница возврата после оплаты расширенной лицензии (ЮKassa return_url).
 * Показывает сообщение об успешной оплате и ждёт активации лицензии (webhook может прийти с задержкой).
 */
const ExtendedLicenseReturnPage: React.FC = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState<'checking' | 'success' | 'success_pending' | 'logged_out'>('checking');
    const pollCountRef = useRef(0);

    useEffect(() => {
        let cancelled = false;

        const checkLicense = async (): Promise<{ canCreateTournament: boolean } | null> => {
            try {
                const res = await apiService.get(API_ENDPOINTS.PAYMENTS.EXTENDED_LICENSE_INFO);
                const data = (res.data as any)?.data;
                return data ? { canCreateTournament: data.canCreateTournament } : null;
            } catch {
                return null;
            }
        };

        const pollUntilActive = async () => {
            const start = Date.now();
            while (!cancelled && Date.now() - start < POLL_TIMEOUT_MS) {
                const result = await checkLicense();
                if (cancelled) return;
                if (result?.canCreateTournament) {
                    setStatus('success');
                    return;
                }
                pollCountRef.current += 1;
                await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
            }
            if (!cancelled) setStatus('success_pending');
        };

        (async () => {
            try {
                // Синхронизация с ЮKassa и проставление extendedLicense (webhook может не дойти, например на localhost)
                await apiService.post(API_ENDPOINTS.PAYMENTS.SYNC_EXTENDED_LICENSE).catch(() => null);
                if (cancelled) return;
                const result = await checkLicense();
                if (cancelled) return;
                if (result === null) {
                    setStatus('logged_out');
                    return;
                }
                if (result.canCreateTournament) {
                    setStatus('success');
                    return;
                }
                setStatus('success_pending');
                pollUntilActive();
            } catch {
                if (!cancelled) setStatus('logged_out');
            }
        })();

        return () => { cancelled = true; };
    }, [navigate]);

    if (status === 'checking') {
        return (
            <PageContainer>
                <Result
                    status="success"
                    title="Оплата прошла успешно!"
                    subTitle="Ожидаем активацию расширенной лицензии..."
                    extra={<Spin size="large" />}
                />
            </PageContainer>
        );
    }

    if (status === 'logged_out') {
        return (
            <PageContainer>
                <Result
                    status="warning"
                    title="Сессия истекла"
                    subTitle="Войдите в аккаунт. Если оплата прошла успешно, расширенная лицензия уже активна — вы сможете создавать турниры."
                    extra={
                        <Button type="primary" onClick={() => navigate('/login')}>
                            Войти
                        </Button>
                    }
                />
            </PageContainer>
        );
    }

    if (status === 'success') {
        return (
            <PageContainer>
                <Result
                    status="success"
                    title="Оплата прошла успешно!"
                    subTitle="Расширенная лицензия активна. Теперь вы можете создавать турниры."
                    extra={[
                        <Button type="primary" key="create" onClick={() => navigate('/tournaments/create', { replace: true })}>
                            Создать турнир
                        </Button>,
                        <Button key="list" onClick={() => navigate('/tournaments', { replace: true })}>
                            К списку турниров
                        </Button>
                    ]}
                />
            </PageContainer>
        );
    }

    // success_pending — лицензия ещё не пришла по webhook, но оплата принята
    return (
        <PageContainer>
            <Result
                status="success"
                title="Оплата прошла успешно!"
                subTitle="Лицензия обычно активируется в течение минуты. Вы можете перейти к турнирам и попробовать создать турнир через минуту, либо обновить страницу позже."
                extra={[
                    <Button type="primary" key="tournaments" onClick={() => navigate('/tournaments', { replace: true })}>
                        К списку турниров
                    </Button>,
                    <Button key="retry" onClick={() => window.location.reload()}>
                        Проверить снова
                    </Button>
                ]}
            />
        </PageContainer>
    );
};

export default ExtendedLicenseReturnPage;
